import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { ObserverService } from './observer.service';
import { APIService } from './api.service';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';

@Injectable()
export class EventService {

    constructor(
        public alertController: AlertController,
        private router: Router,
        private authService: AuthService,
        private observerService: ObserverService,
        private apiService: APIService,
        private databaseService: DatabaseService,
        private storageService: StorageService
    ) { }

    addCategories(categories, raw_categories, parent_category_id) {
        for (let i = 0; i < raw_categories.length; i++) {
            let category = {};
            category["id"] = raw_categories[i].id;
            category["parent_category_id"] = parent_category_id;
            category["title"] = raw_categories[i].title;
            category['order'] = i;
            categories.push(category);
            if (raw_categories[i].categories) {
                this.addCategories(categories, raw_categories[i].categories, raw_categories[i].id);
            }
        }
        return categories;
    }

    addEvent(raw_event) {
        return new Promise<void>((resolve, reject) => {
            let event = {};
            let event_categories = [];
            event['id'] = raw_event.id;
            event['title'] = raw_event.title;
            event['allow_sessions'] = raw_event.allow_sessions;
            event['allow_session_dates'] = raw_event.allow_session_dates;
            event['allow_session_venues'] = raw_event.allow_session_venues;
            event['ticket_count'] = raw_event.ticket_count;
            if (raw_event.min_start_date) {
                event['min_start_date'] = raw_event.min_start_date;
            }
            if (raw_event.min_start_date_timezone) {
                event['min_start_date_timezone'] = raw_event.min_start_date_timezone;
            }
            if (raw_event.start_date) {
                event['start_date'] = raw_event.start_date;
            }
            if (raw_event.max_end_date) {
                event['max_end_date'] = raw_event.max_end_date;
            }
            if (raw_event.max_end_date_timezone) {
                event['max_end_date_timezone'] = raw_event.max_end_date_timezone;
            }
            if (raw_event.end_date) {
                event['end_date'] = raw_event.end_date;
            }
            if (raw_event.timezone) {
                event['timezone'] = raw_event.timezone;
            }
            if (raw_event.images) {
                event['images'] = raw_event.images;
            }
            if (raw_event.venue) {
                event['venue'] = raw_event.venue;
            }
            if (raw_event.max_tickets !== undefined) {
                event['max_tickets'] = raw_event.max_tickets;
                event['tickets_remaining'] = raw_event.max_tickets - raw_event.ticket_count;
            }
            if (raw_event.code) {
                event['code'] = raw_event.code;
            }
            if (raw_event.categories) {
                for (let l = 0; l < raw_event.categories.length; l++) {
                    let event_category = {};
                    event_category["id"] = raw_event.id + raw_event.categories.id;
                    event_category["event_id"] = raw_event.id;
                    event_category["category_id"] = raw_event.categories.id;
                    event_categories.push(event_category);
                }
            }
            if (raw_event.low_ticket_threshold !== undefined) {
                event['low_ticket_threshold'] = raw_event.low_ticket_threshold;
            }
            this.databaseService.event_event
                .add(event)
                .then(() => {
                    this.databaseService.event_event_category
                        .bulkAdd(event_categories)
                        .then(() => {
                            if (raw_event.categories) {
                                event['categories'] = raw_event.categories;
                            }
                            this.observerService.publish("event_added", event);
                            this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                })
                .catch(() => {
                    reject();
                });
        });
    }

    addEventSession(raw_event_session) {
        return new Promise<void>((resolve, reject) => {
            let event_session = {};
            event_session['id'] = raw_event_session.id;
            event_session['event_id'] = raw_event_session.event.id;
            event_session['ticket_count'] = raw_event_session.ticket_count;
            event_session['order'] = raw_event_session.order;
            if (raw_event_session.title) {
                event_session['title'] = raw_event_session.title;
            }
            if (raw_event_session.start_date) {
                event_session['start_date'] = raw_event_session.start_date;
            }
            if (raw_event_session.end_date) {
                event_session['end_date'] = raw_event_session.end_date;
            }
            if (raw_event_session.timezone) {
                event_session['timezone'] = raw_event_session.timezone;
            }
            if (raw_event_session.venue) {
                event_session['venue'] = raw_event_session.venue;
            }
            if (raw_event_session.max_tickets !== undefined) {
                event_session['max_tickets'] = raw_event_session.max_tickets;
                event_session['tickets_remaining'] = raw_event_session.max_tickets - raw_event_session.ticket_count;
            }
            if (raw_event_session.low_ticket_threshold !== undefined) {
                event_session['low_ticket_threshold'] = raw_event_session.low_ticket_threshold;
            }
            this.databaseService.event_event_session
                .add(event_session)
                .then(() => {
                    this.observerService.publish("event_session_added", event_session);
                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                    resolve();
                })
                .catch((error) => {
                    reject();
                });
        });
    }

    addTicketType(raw_ticket_type) {
        return new Promise<void>((resolve, reject) => {
            let ticket_type = {};
            ticket_type['id'] = raw_ticket_type.id;
            ticket_type['event_id'] = raw_ticket_type.event.id;
            ticket_type['title'] = raw_ticket_type.title;
            ticket_type['tax_type'] = raw_ticket_type.tax_type;
            ticket_type['prices'] = raw_ticket_type.prices;
            ticket_type['ticket_count'] = raw_ticket_type.ticket_count;
            ticket_type['order'] = raw_ticket_type.order;
            if (raw_ticket_type.event_session_id) {
                ticket_type['event_session_id'] = raw_ticket_type.session.id;
            }
            if (raw_ticket_type.max_tickets !== undefined) {
                ticket_type['max_tickets'] = raw_ticket_type.max_tickets;
                ticket_type['tickets_remaining'] = raw_ticket_type.max_tickets - raw_ticket_type.ticket_count;
            }
            if (raw_ticket_type.low_ticket_threshold !== undefined) {
                ticket_type['low_ticket_threshold'] = raw_ticket_type.low_ticket_threshold;
            }
            this.databaseService.event_ticket_type
                .add(ticket_type)
                .then(() => {
                    this.observerService.publish("ticket_type_added", ticket_type);
                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    }

    bulkAddEventCategories(raw_event_categories) {
        return new Promise<void>((resolve, reject) => {
            if(raw_event_categories !== undefined) {    
                this.databaseService.event_category
                    .clear()
                    .then(() => {
                        let categories = this.addCategories([], raw_event_categories, null);
                        this.databaseService.event_category
                            .bulkAdd(categories)
                            .then(() => {
                                this.observerService.publish("event_categories_changed", null);
                                this.storageService.set("event_categories_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                    })
                    .catch(() => {
                        reject();
                    });
            }
            else {
                resolve();
            }
        });
    }

    bulkAddEvents(raw_events) {
        return new Promise<void>((resolve, reject) => {
            if(raw_events !== undefined) {
                let events = [];
                let sessions = [];
                let ticket_types = [];
                let event_categories = [];
                for (let i = 0; i < raw_events.length; i++) {
                    let event = {};
                    event['id'] = raw_events[i].id;
                    event['title'] = raw_events[i].title;
                    event['allow_sessions'] = raw_events[i].allow_sessions;
                    event['allow_session_dates'] = raw_events[i].allow_session_dates;
                    event['allow_session_venues'] = raw_events[i].allow_session_venues;
                    event['ticket_count'] = raw_events[i].ticket_count;
                    if (raw_events[i].min_start_date) {
                        event['min_start_date'] = raw_events[i].min_start_date;
                    }
                    if (raw_events[i].min_start_date_timezone) {
                        event['min_start_date_timezone'] = raw_events[i].min_start_date_timezone;
                    }
                    if (raw_events[i].start_date) {
                        event['start_date'] = raw_events[i].start_date;
                    }
                    if (raw_events[i].max_end_date) {
                        event['max_end_date'] = raw_events[i].max_end_date;
                    }
                    if (raw_events[i].max_end_date_timezone) {
                        event['max_end_date_timezone'] = raw_events[i].max_end_date_timezone;
                    }
                    if (raw_events[i].end_date) {
                        event['end_date'] = raw_events[i].end_date;
                    }
                    if (raw_events[i].timezone) {
                        event['timezone'] = raw_events[i].timezone;
                    }
                    if (raw_events[i].images) {
                        event['images'] = raw_events[i].images;
                    }
                    if (raw_events[i].venue) {
                        event['venue'] = raw_events[i].venue;
                    }
                    if (raw_events[i].max_tickets !== undefined) {
                        event['max_tickets'] = raw_events[i].max_tickets;
                        event['tickets_remaining'] = raw_events[i].max_tickets - raw_events[i].ticket_count;
                    }
                    if (raw_events[i].code) {
                        event['code'] = raw_events[i].code;
                    }
                    if (raw_events[i].low_ticket_threshold !== undefined) {
                        event['low_ticket_threshold'] = raw_events[i].low_ticket_threshold;
                    }
                    if (raw_events[i].sessions) {
                        for (let j = 0; j < raw_events[i].sessions.length; j++) {
                            let session = {};
                            session['id'] = raw_events[i].sessions[j].id;
                            session['event_id'] = raw_events[i].id;
                            session['ticket_count'] = raw_events[i].sessions[j].ticket_count;
                            session['order'] = raw_events[i].sessions[j].ticket_count;
                            if (raw_events[i].sessions[j].title) {
                                session['title'] = raw_events[i].sessions[j].title;
                            }
                            if (raw_events[i].sessions[j].start_date) {
                                session['start_date'] = raw_events[i].sessions[j].start_date;
                            }
                            if (raw_events[i].sessions[j].end_date) {
                                session['end_date'] = raw_events[i].sessions[j].end_date;
                            }
                            if (raw_events[i].sessions[j].timezone) {
                                session['timezone'] = raw_events[i].sessions[j].timezone;
                            }
                            if (raw_events[i].sessions[j].venue) {
                                session['venue'] = raw_events[i].sessions[j].venue;
                            }
                            if (raw_events[i].sessions[j].max_tickets !== undefined) {
                                session['max_tickets'] = raw_events[i].sessions[j].max_tickets;
                                session['tickets_remaining'] = raw_events[i].sessions[j].max_tickets - raw_events[i].sessions[j].ticket_count;
                            }
                            if (raw_events[i].sessions[j].low_ticket_threshold !== undefined) {
                                session['low_ticket_threshold'] = raw_events[i].sessions[j].low_ticket_threshold;
                            }
                            if (raw_events[i].sessions[j].ticket_types) {
                                for (let k = 0; k < raw_events[i].sessions[j].ticket_types.length; k++) {
                                    let ticket_type = {};
                                    ticket_type['id'] = raw_events[i].sessions[j].ticket_types[k].id;
                                    ticket_type['event_id'] = raw_events[i].id;
                                    ticket_type['event_session_id'] = raw_events[i].sessions[j].id;
                                    ticket_type['title'] = raw_events[i].sessions[j].ticket_types[k].title;
                                    ticket_type['tax_type'] = raw_events[i].sessions[j].ticket_types[k].tax_type;
                                    ticket_type['prices'] = raw_events[i].sessions[j].ticket_types[k].prices;
                                    ticket_type['ticket_count'] = raw_events[i].sessions[j].ticket_types[k].ticket_count;
                                    ticket_type['order'] = raw_events[i].sessions[j].ticket_types[k].order;
                                    if (raw_events[i].sessions[j].ticket_types[k].max_tickets !== undefined) {
                                        ticket_type['max_tickets'] = raw_events[i].sessions[j].ticket_types[k].max_tickets;
                                        ticket_type['tickets_remaining'] = raw_events[i].sessions[j].ticket_types[k].max_tickets - raw_events[i].sessions[j].ticket_types[k].ticket_count;
                                    }
                                    if (raw_events[i].sessions[j].ticket_types[k].low_ticket_threshold !== undefined) {
                                        ticket_type['low_ticket_threshold'] = raw_events[i].sessions[j].ticket_types[k].low_ticket_threshold;
                                    }
                                    ticket_types.push(ticket_type);
                                }
                            }
                            sessions.push(session);
                        }
                    }
                    if (raw_events[i].ticket_types) {
                        for (let k = 0; k < raw_events[i].ticket_types.length; k++) {
                            let ticket_type = {};
                            ticket_type['id'] = raw_events[i].ticket_types[k].id;
                            ticket_type['event_id'] = raw_events[i].id;
                            ticket_type['title'] = raw_events[i].ticket_types[k].title;
                            ticket_type['tax_type'] = raw_events[i].ticket_types[k].tax_type;
                            ticket_type['prices'] = raw_events[i].ticket_types[k].prices;
                            ticket_type['ticket_count'] = raw_events[i].ticket_types[k].ticket_count;
                            ticket_type['order'] = raw_events[i].ticket_types[k].order;
                            if (raw_events[i].ticket_types[k].max_tickets !== undefined) {
                                ticket_type['max_tickets'] = raw_events[i].ticket_types[k].max_tickets;
                                ticket_type['tickets_remaining'] = raw_events[i].ticket_types[k].max_tickets - raw_events[i].ticket_types[k].ticket_count;
                            }
                            if (raw_events[i].ticket_types[k].low_ticket_threshold !== undefined) {
                                ticket_type['low_ticket_threshold'] = raw_events[i].ticket_types[k].low_ticket_threshold;
                            }
                            ticket_types.push(ticket_type);
                        }
                    }
                    if (raw_events[i].categories) {
                        for (let l = 0; l < raw_events[i].categories.length; l++) {
                            let event_category = {};
                            event_category["id"] = raw_events[i].id + raw_events[i].categories[l].id;
                            event_category["event_id"] = raw_events[i].id;
                            event_category["category_id"] = raw_events[i].categories[l].id;
                            event_categories.push(event_category);
                        }
                    }
                    events.push(event);
                }
                this.databaseService.event_event_category
                    .clear()
                    .then(() => {
                        this.databaseService.event_ticket_type
                            .clear()
                            .then(() => {
                                this.databaseService.event_event_session
                                    .clear()
                                    .then(() => {
                                        this.databaseService.event_event
                                            .clear()
                                            .then(async () => {
                                                await this.databaseService.event_event
                                                    .bulkAdd(events)
                                                    .then(() => {
                                                        // do nothing
                                                    })
                                                    .catch(() => {
                                                        reject();
                                                    });
                                                await this.databaseService.event_event_session
                                                    .bulkAdd(sessions)
                                                    .then(() => {
                                                        // do nothing
                                                    })
                                                    .catch(() => {
                                                        reject();
                                                    });
                                                await this.databaseService.event_ticket_type
                                                    .bulkAdd(ticket_types)
                                                    .then(() => {
                                                        // do nothing
                                                    })
                                                    .catch(() => {
                                                        reject();
                                                    });
                                                await this.databaseService.event_event_category
                                                    .bulkAdd(event_categories)
                                                    .then(() => {
                                                        // do nothing
                                                    })
                                                    .catch(() => {
                                                        reject();
                                                    });
                                                this.observerService.publish("events_changed", null);
                                                this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                                                resolve();
                                            })
                                            .catch(() => {
                                                reject();
                                            });
                                    })
                                    .catch(() => {
                                        reject();
                                    });
                            })
                            .catch(() => {
                                reject();
                            });
                    })
                    .catch(() => {
                        reject();
                    });
            }
            else {
                resolve();
            }
        });
    }    

    deleteEvent(event_id) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.event_event
                .delete(event_id)
                .then(() => {
                    this.databaseService.event_event_category
                        .where("event_id")
                        .equals(event_id)
                        .delete()
                        .then(() => {
                            this.observerService.publish("event_deleted", event_id);
                            this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                })
                .catch(() => {
                    reject();
                });
        });
    }

    deleteEventSession(event_session) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.event_event_session
                .delete(event_session.id)
                .then(() => {
                    this.observerService.publish("event_session_deleted", event_session);
                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    }

    deleteTicketType(ticket_type) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.event_ticket_type
                .delete(ticket_type.id)
                .then(() => {
                    this.observerService.publish("ticket_type_deleted", ticket_type);
                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getCategory(category_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_category
                .get(category_id)
                .then((category) => {
                    resolve(category);
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getCategoryChildren(parent_category_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_category
                .where("parent_category_id")
                .equals(parent_category_id)
                .toArray()
                .then((categories) => {
                    resolve(categories);
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getCategoryAncestors(ancestors, parent_category_id) {
        return new Promise((resolve, reject) => {
            if (parent_category_id != null) {
                this.databaseService.event_category
                    .get(parent_category_id)
                    .then((category) => {
                        ancestors.unshift(category);
                        this.getCategoryAncestors(ancestors, category.parent_category_id).then((ancestors) => {
                            resolve(ancestors);
                        });
                    });
            }
            else {
                resolve(ancestors);
            }
        });
    }

    getCategoryBreadcrumbs(category_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_category
                .get(category_id)
                .then((category) => {
                    let breadcrumbs = [];
                    if (category) {
                        breadcrumbs.push(category);
                        this.getCategoryAncestors(breadcrumbs, category.parent_category_id).then((breadcrumbs) => {
                            resolve(breadcrumbs);
                        });
                    }
                    else {
                        resolve(breadcrumbs);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getEvent(event_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_event
                .get(event_id)
                .then((event) => {
                    resolve(event);
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getEventsByCategory(category_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_event_category
                .where("category_id")
                .equals(category_id)
                .toArray()
                .then((junctions) => {
                    let event_ids = [];
                    for (let i = 0; i < junctions.length; i++) {
                        event_ids.push(junctions[i].event_id);
                    }
                    this.databaseService.event_event
                        // NOTE: We don't use bulkGet as conflicts with Angular model binding somehow
                        .toArray()
                        .then((events) => {
                            let found_events = [];
                            for (let i = 0; i < events.length; i++) {
                                if (event_ids.includes(events[i].id)) {
                                    found_events.push(events[i]);
                                }
                            }
                            resolve(found_events);
                        })
                        .catch(() => {
                            reject();
                        });
                });
        });
    }

    getEventsByKeywords(keywords) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_event
                // NOTE: We don't use bulkGet as conflicts with Angular model binding somehow
                .toArray()
                .then((events) => {
                    let found_events = [];
                    for (let i = 0; i < events.length; i++) {
                        let event = events[i];
                        event['type'] = 'event';
                        if (event['title'].toString().toLowerCase().indexOf(keywords) !== -1) {
                            found_events.push(event);
                        }
                        else if (event['code'] && event['code'].toString().toLowerCase().indexOf(keywords) !== -1) {
                            found_events.push(event);
                        }
                    }
                    resolve(found_events);
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getEventSession(event_session_id) {
        return new Promise((resolve, reject) => {
            if (event_session_id) {
                this.databaseService.event_event_session
                    .get(event_session_id)
                    .then((event_session) => {
                        resolve(event_session);
                    })
                    .catch(() => {
                        reject();
                    });
            }
            else {
                resolve(null);
            }
        });
    }

    getEventSessions(event_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_event_session
                .where("event_id")
                .equals(event_id)
                .toArray()
                .then((event_sessions) => {
                    resolve(event_sessions);
                })
                .catch(() => {
                    reject();
                });
        });
    }

    getTicketType(ticket_type_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_ticket_type.get(ticket_type_id).then((ticket_type) => {
                resolve(ticket_type);
            })
            .catch(() => {
                reject();
            });
        });
    }

    getEventTicketTypes(event_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_ticket_type.where("event_id").equals(event_id).toArray().then((ticket_types) => {
                resolve(ticket_types);
            })
            .catch(() => {
                reject();
            });
        });
    }

    getEventSessionTicketTypes(event_session_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.event_ticket_type.where("event_session_id").equals(event_session_id).toArray().then((ticket_types) => {
                resolve(ticket_types);
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadEventCategories() {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getEventCategories({}).then((raw_event_categories) => {
                    this.bulkAddEventCategories(raw_event_categories).then(() => {
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the event categories.");
                    } 
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the event categories.");
                    } 
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the event categories at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateEvent(raw_event) {
        return new Promise<void>((resolve, reject) => {
            this.getEvent(raw_event.id).then((event) => {
                if (event) {
                    this.databaseService.event_event
                        .delete(raw_event.id)
                        .then(() => {
                            this.databaseService.event_event_category
                                .where("event_id")
                                .equals(raw_event.id)
                                .delete()
                                .then(() => {
                                    let event = {};
                                    let event_categories = [];
                                    event['id'] = raw_event.id;
                                    event['title'] = raw_event.title;
                                    event['allow_sessions'] = raw_event.allow_sessions;
                                    event['allow_session_dates'] = raw_event.allow_session_dates;
                                    event['allow_session_venues'] = raw_event.allow_session_venues;
                                    event['ticket_count'] = raw_event.ticket_count;
                                    if (raw_event.min_start_date) {
                                        event['min_start_date'] = raw_event.min_start_date;
                                    }
                                    if (raw_event.min_start_date_timezone) {
                                        event['min_start_date_timezone'] = raw_event.min_start_date_timezone;
                                    }
                                    if (raw_event.start_date) {
                                        event['start_date'] = raw_event.start_date;
                                    }
                                    if (raw_event.max_end_date) {
                                        event['max_end_date'] = raw_event.max_end_date;
                                    }
                                    if (raw_event.max_end_date_timezone) {
                                        event['max_end_date_timezone'] = raw_event.max_end_date_timezone;
                                    }
                                    if (raw_event.end_date) {
                                        event['end_date'] = raw_event.end_date;
                                    }
                                    if (raw_event.timezone) {
                                        event['timezone'] = raw_event.timezone;
                                    }
                                    if (raw_event.images) {
                                        event['images'] = raw_event.images;
                                    }
                                    if (raw_event.venue) {
                                        event['venue'] = raw_event.venue;
                                    }
                                    if (raw_event.max_tickets !== undefined) {
                                        event['max_tickets'] = raw_event.max_tickets;
                                        event['tickets_remaining'] = raw_event.max_tickets - raw_event.ticket_count;
                                    }
                                    else if (raw_event.max_tickets === undefined) {
                                        event['tickets_remaining'] = null;
                                    }
                                    if (event['tickets_remaining'] < 0) {
                                        event['tickets_remaining'] = 0;
                                    }
                                    if (raw_event.code) {
                                        event['code'] = raw_event.code;
                                    }
                                    if (raw_event.categories) {
                                        for (let l = 0; l < raw_event.categories.length; l++) {
                                            let event_category = {};
                                            event_category["id"] = raw_event.id + raw_event.categories.id;
                                            event_category["event_id"] = raw_event.id;
                                            event_category["category_id"] = raw_event.categories.id;
                                            event_categories.push(event_category);
                                        }
                                    }
                                    this.databaseService.event_event
                                        .add(event)
                                        .then(() => {
                                            this.databaseService.event_event_category
                                                .bulkAdd(event_categories)
                                                .then(() => {
                                                    if (raw_event.categories) {
                                                        event['categories'] = raw_event.categories;
                                                    }
                                                    this.observerService.publish("event_updated", event);
                                                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                                                    resolve();
                                                })
                                                .catch(() => {
                                                    reject();
                                                });
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                })
                                .catch(() => {
                                    reject();
                                });
                        })
                        .catch(() => {
                            reject();
                        });
                } else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateEventSession(raw_event_session) {
        return new Promise<void>((resolve, reject) => {
            this.getEventSession(raw_event_session.id).then((event_session) => {
                if (event_session) {
                    this.databaseService.event_event_session.delete(raw_event_session.id).then(() => {
                        let event_session = {};
                        event_session['id'] = raw_event_session.id;
                        event_session['event_id'] = raw_event_session.event.id;
                        event_session['ticket_count'] = raw_event_session.ticket_count;
                        event_session['order'] = raw_event_session.order;
                        if (raw_event_session.title) {
                            event_session['title'] = raw_event_session.title;
                        }
                        if (raw_event_session.start_date) {
                            event_session['start_date'] = raw_event_session.start_date;
                        }
                        if (raw_event_session.end_date) {
                            event_session['end_date'] = raw_event_session.end_date;
                        }
                        if (raw_event_session.timezone) {
                            event_session['timezone'] = raw_event_session.timezone;
                        }
                        if (raw_event_session.venue) {
                            event_session['venue'] = raw_event_session.venue;
                        }
                        if (raw_event_session.max_tickets !== undefined) {
                            event_session['max_tickets'] = raw_event_session.max_tickets;
                            event_session['tickets_remaining'] = raw_event_session.max_tickets - raw_event_session.ticket_count;
                        } else if (raw_event_session.max_tickets === undefined) {
                            event_session['tickets_remaining'] = null;
                        }
                        if (event_session['tickets_remaining'] < 0) {
                            event_session['tickets_remaining'] = 0;
                        }
                        this.databaseService.event_event_session.add(event_session).then(() => {
                            this.observerService.publish("event_session_updated", event_session);
                            this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => { }).catch(() => { });
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    })
                    .catch(() => {
                        reject();
                    });
                } else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateTicketType(raw_ticket_type) {
        return new Promise<void>((resolve, reject) => {
            this.getTicketType(raw_ticket_type.id).then((ticket_type) => {
                if(ticket_type) {

                    let ticket_type = {};
                    ticket_type['id'] = raw_ticket_type.id;
                    ticket_type['event_id'] = raw_ticket_type.event.id;
                    ticket_type['title'] = raw_ticket_type.title;
                    ticket_type['tax_type'] = raw_ticket_type.tax_type;
                    ticket_type['ticket_count'] = raw_ticket_type.ticket_count;
                    ticket_type['order'] = raw_ticket_type.order;

                    if (raw_ticket_type.session) {
                        ticket_type['event_session_id'] = raw_ticket_type.session.id;
                    }
                    else {
                        ticket_type['event_session_id'] = null;
                    }

                    if (raw_ticket_type.prices) {
                        ticket_type['prices'] = raw_ticket_type.prices;
                    }
                    else {
                        ticket_type['prices'] = null;
                    }

                    if (raw_ticket_type.max_tickets !== undefined) {
                        ticket_type['max_tickets'] = raw_ticket_type.max_tickets;
                        ticket_type['tickets_remaining'] = raw_ticket_type.max_tickets - raw_ticket_type.ticket_count;
                    }
                    else if (raw_ticket_type.max_tickets === undefined) {
                        ticket_type['max_tickets'] = null;
                        ticket_type['tickets_remaining'] = null;
                    }

                    if (ticket_type['tickets_remaining'] < 0) {
                        ticket_type['tickets_remaining'] = 0;
                    }

                    this.databaseService.event_ticket_type.update(raw_ticket_type.id, ticket_type).then(() => {
                        this.observerService.publish("ticket_type_updated", ticket_type);
                        this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => { }).catch(() => { });
                        this.getEvent(raw_ticket_type.event.id).then((event: any) => {
                            this.getEventTicketTypes(event.id).then((ticket_types: any) => {
                                let event_ticket_count = 0;
                                for (let i = 0; i < ticket_types.length; i++) {
                                    event_ticket_count += ticket_types[i].ticket_count;
                                }
                                event.ticket_count = event_ticket_count;
                                if (event.max_tickets !== undefined) {
                                    event['tickets_remaining'] = event.max_tickets - event.ticket_count;
                                }
                                this.databaseService.event_event.update(event.id, event).then(() => {
                                    this.observerService.publish("event_ticket_count_updated", event);
                                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => { }).catch(() => { });
                                    if (ticket_type['event_session_id']) {
                                        this.getEventSession(ticket_type['event_session_id']).then((session: any) => {
                                            this.getEventSessionTicketTypes(session.id).then((ticket_types: any) => {
                                                let session_ticket_count = 0;
                                                for (let i = 0; i < ticket_types.length; i++) {
                                                    session_ticket_count += ticket_types[i].ticket_count;
                                                }
                                                session.ticket_count = session_ticket_count;
                                                if (session.max_tickets !== undefined) {
                                                    session['tickets_remaining'] = session.max_tickets - session.ticket_count;
                                                }
                                                this.databaseService.event_event_session.update(session.id, session).then(() => {
                                                    this.observerService.publish("event_session_updated", session);
                                                    this.storageService.set("events_last_updated_date", new Date().toISOString()).then(() => { }).catch(() => { });
                                                })
                                                .catch(() => {
                                                    reject();
                                                });
                                            })
                                            .catch(() => {
                                                reject();
                                            });
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                })
                                .catch(() => {
                                    reject();
                                });
                            })
                            .catch(() => {
                                reject();
                            });
                        })
                        .catch(() => {
                            reject();
                        });
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

}
